import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyCell } from '../../../components/EmptyCell'
import { EmptyState } from '../../../components/EmptyState'
import { FormField } from '../../../components/FormField'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { statusOptions } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { formatIsoDate, useHasRole, useAppToast } from '../../../helpers'
import {
  IScheduleClass,
  IScheduleClassFilter,
  IScheduleFormOptions,
  TScheduleFormat,
  TScheduleShift,
  TScheduleStatus,
  TScheduleTerm,
  TScheduleWeekDay
} from '../../../services/ScheduleClasses/interfaces'
import ScheduleClassService from '../../../services/ScheduleClasses/ScheduleClassService'
import {
  courseIssue,
  FORMAT_LABELS,
  FORMAT_SHORT_LABELS,
  SCHEDULE_STATUS,
  SHIFT_LABELS,
  SHIFT_SHORT_LABELS,
  teacherIssue,
  TEACHER_PLACEHOLDER,
  WEEK_DAY_LABELS,
  WEEK_DAY_SHORT_LABELS,
  WEEK_DAYS
} from './constants'
import ScheduleClassFormModal from './ScheduleClassFormModal'
import ScheduleClassStatusCell from './ScheduleClassStatusCell'

interface IProps {
  year: number
  term: TScheduleTerm
  options: IScheduleFormOptions
  /** Chamado depois de criar, editar ou excluir, para o pai refazer os contadores das abas. */
  onMutate: () => void
  /**
   * Leva a tela para outro ano/período: o período da turma sai da data de início, então ela
   * pode não pertencer à aba a partir da qual o formulário foi aberto.
   */
  onGoToPeriod: (term: TScheduleTerm | null, year: number | null) => void
}

/**
 * A busca fica fora do filtro de propósito: é o único campo digitado caractere a caractere,
 * e por isso o único que precisa de debounce. Mantê-la aqui faria cada tecla mudar a
 * identidade do filtro e disparar a busca da lista.
 */
const baseFilter: Omit<IScheduleClassFilter, 'search'> = {
  course_id: '',
  teacher_id: '',
  status: [],
  shift: '',
  days: [],
  format: '',
  only_incomplete: false,
  only_upcoming: false,
  page: 1,
  page_size: DEFAULT_PAGE_SIZE
}

// Sigla e Sigla SIFESC saíram da tabela com a importação de planilha (ABRA-315).
const COLUMN_COUNT = 13

/**
 * Célula que carrega um aviso de vínculo quebrado à esquerda do seu conteúdo.
 *
 * Curso e professor mostram o mesmo ícone pelo mesmo motivo, e desenhá-lo duas vezes na tabela
 * é como o aviso do professor tinha ido parar na coluna do curso: o `<Tooltip>` do Chakra
 * precisa de um filho que aceite ref, daí o `<Box as="span">` em vez do `<i>` direto.
 */
function IssueCell({ issue, children }: { issue: string | null; children: ReactNode }) {
  return (
    <Flex align="center" gap={2}>
      {issue && (
        <Tooltip label={issue}>
          <Box as="span" color="orange.400" aria-label={issue}>
            <Icon name="lni lni-warning" />
          </Box>
        </Tooltip>
      )}
      {children}
    </Flex>
  )
}

/**
 * Listagem de turmas de um período (ABRA-181).
 *
 * Filtros, busca e paginação são todos server-side: um período tem dezenas de turmas e a
 * lista cresce a cada semestre, então filtrar o array já carregado não se sustentaria.
 */
export default function ScheduleClassesTable({ year, term, options, onMutate, onGoToPeriod }: IProps) {
  const toast = useAppToast()
  const hasRole = useHasRole()
  const service = useMemo(() => new ScheduleClassService(), [])
  const formModal = useDisclosure()

  const [filter, setFilter] = useState(baseFilter)
  /** O que está digitado no campo. */
  const [search, setSearch] = useState('')
  /** O que já virou requisição — trilha `search` com 500 ms de atraso. */
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [rows, setRows] = useState<IScheduleClass[]>([])
  const [totalFiltered, setTotalFiltered] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const [editing, setEditing] = useState<IScheduleClass | null>(null)
  const [deletingId, setDeletingId] = useState(0)

  const permissions = {
    add: hasRole('add_class_schedule'),
    edit: hasRole('edit_class_schedule'),
    delete: hasRole('delete_class_schedule')
  }

  const totalPages = Math.ceil(totalFiltered / filter.page_size) || 1

  /**
   * Trocar de ano, de período ou de busca reinicia a paginação: a página 3 do resultado
   * anterior provavelmente não existe no novo.
   *
   * O ajuste acontece durante a renderização, e não num efeito, para que saia uma requisição
   * só. Num efeito, a busca da página antiga já teria partido antes do reset — e, sem
   * cancelamento, a resposta atrasada dela ainda poderia sobrescrever a da página 1.
   *
   * Atualizar estado no corpo do componente parece antipadrão, mas é o padrão que a doc do
   * React recomenda para este caso exato ("You Might Not Need an Effect" → ajustar estado
   * quando uma prop muda). As duas condições que o tornam seguro estão satisfeitas: o setState
   * é do próprio componente, nunca de outro, e está atrás de uma comparação que deixa de valer
   * já na re-renderização imediata — não há laço nem efeito colateral fora do render.
   */
  const resetKey = `${year}|${term}|${debouncedSearch}`
  const [lastResetKey, setLastResetKey] = useState(resetKey)

  if (resetKey !== lastResetKey) {
    setLastResetKey(resetKey)
    // Devolver o mesmo objeto quando já se está na página 1 preserva a identidade do filtro.
    setFilter((f) => (f.page === 1 ? f : { ...f, page: 1 }))
  }

  const appliedFilter: IScheduleClassFilter = useMemo(
    () => ({ ...filter, search: debouncedSearch }),
    [filter, debouncedSearch]
  )

  const fetchRows = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const response = await service.list(year, term, appliedFilter)
      setRows(response.data)
      setTotalFiltered(response.total_filtered)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as turmas', error)
    } finally {
      setLoading(false)
    }
  }, [service, year, term, appliedFilter, toast])

  // Sem espera: trocar de aba, paginar ou mexer num select já chega aqui com o valor final.
  // Quem segura o tranco de digitar é o debounce da busca, logo abaixo.
  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 500)
    return () => clearTimeout(timeout)
  }, [search])

  function updateFilter(patch: Partial<Omit<IScheduleClassFilter, 'search'>>) {
    setFilter((f) => ({ ...f, ...patch, page: 1 }))
  }

  /** Marca ou desmarca um dia, mantendo o valor na ordem da semana. */
  function toggleDay(day: TScheduleWeekDay) {
    const next = filter.days.includes(day) ? filter.days.filter((d) => d !== day) : [...filter.days, day]
    updateFilter({ days: WEEK_DAYS.filter((d) => next.includes(d)) })
  }

  function openCreate() {
    setModalMode('create')
    setEditing(null)
    formModal.onOpen()
  }

  function openEdit(row: IScheduleClass) {
    setModalMode('edit')
    setEditing(row)
    formModal.onOpen()
  }

  /**
   * A troca de status já foi confirmada pela API — aqui é só refletir na tela.
   *
   * Repinta a linha em vez de refazer a lista: um refetch pisca a tabela inteira por uma
   * mudança de uma célula. A exceção é o filtro de status ativo, em que a linha pode ter
   * deixado de pertencer ao resultado e precisa sair dele.
   *
   * 'onMutate' não entra: os contadores das abas são por período, e status não muda período.
   */
  function handleStatusChanged(id: number, status: TScheduleStatus) {
    if (filter.status.length > 0) {
      fetchRows()
      return
    }

    setRows((current) => current.map((row) => (row.id === id ? { ...row, status } : row)))
  }

  async function handleDelete() {
    try {
      await service.remove(deletingId)
      toast.success('Turma excluída com sucesso!')

      // Excluir a última linha de uma página > 1 deixava a busca repetir uma página agora vazia:
      // a tabela e o paginador sumiam atrás de "Não há turmas cadastradas para este período",
      // mensagem falsa, já que as páginas anteriores continuam cheias. Trocar a página dispara
      // o efeito de busca sozinho, por isso não há fetchRows() neste ramo.
      if (rows.length === 1 && filter.page > 1) {
        setFilter((f) => ({ ...f, page: f.page - 1 }))
      } else {
        fetchRows()
      }

      onMutate()
    } catch {
      toast.error('Erro ao excluir turma')
    } finally {
      setDeletingId(0)
    }
  }

  const isEmpty = !loading && rows.length === 0
  const hasActiveFilters =
    debouncedSearch !== '' ||
    filter.course_id !== '' ||
    filter.teacher_id !== '' ||
    filter.status.length > 0 ||
    filter.shift !== '' ||
    filter.days.length > 0 ||
    filter.format !== '' ||
    filter.only_incomplete ||
    filter.only_upcoming

  return (
    <Box>
      <Flex gap={3} flexWrap="wrap" mb={3} align="flex-end">
        {/*
          A busca livre é pelo código, e o rótulo diz só isso: curso e professor têm select
          próprio, que filtra por id e não erra por grafia nem por homônimo. O back ainda
          varre course_name_raw/teacher_name_raw, mas esse texto cru só existe nas turmas
          antigas que nunca foram vinculadas — anunciar "nome" no campo prometia uma busca
          por nome que não vale para nenhuma turma cadastrada pelo painel.
        */}
        <FormField label="Buscar" maxW="240px">
          <Input placeholder="Código" value={search} onChange={(e) => setSearch(e.target.value)} />
        </FormField>

        <FormField label="Curso" maxW="240px">
          <SearchableSelect
            options={options.courses.map((c) => ({ value: String(c.id), label: c.name }))}
            value={filter.course_id === '' ? '' : String(filter.course_id)}
            onChange={(value) => updateFilter({ course_id: value === '' ? '' : Number(value) })}
            placeholder="Todos os cursos"
            isClearable
          />
        </FormField>

        <FormField label="Professor" maxW="240px">
          <SearchableSelect
            options={options.teachers.map((t) => ({ value: String(t.id), label: t.name }))}
            value={filter.teacher_id === '' ? '' : String(filter.teacher_id)}
            onChange={(value) => updateFilter({ teacher_id: value === '' ? '' : Number(value) })}
            placeholder="Todos os professores"
            isClearable
          />
        </FormField>

        <FormField label="Status" maxW="240px">
          <SearchableSelect
            isMulti
            options={statusOptions(SCHEDULE_STATUS)}
            value={filter.status}
            onChange={(value) => updateFilter({ status: value as TScheduleStatus[] })}
            placeholder="Todos"
            isClearable
          />
        </FormField>

        <FormField label="Turno" maxW="180px">
          <FormSelect
            value={filter.shift}
            onChange={(value) => updateFilter({ shift: value as TScheduleShift | '' })}
            options={Object.entries(SHIFT_LABELS).map(([value, label]) => ({ value: value, label: label }))}
            placeholder="Todos"
          />
        </FormField>

        {/*
          Botões e não um select múltiplo: com vários dias marcados as etiquetas do select quebravam
          em duas ou três linhas e a fileira de filtros crescia junto. Sete botões curtos cabem numa
          linha só, na altura dos campos ao lado, e mostram de relance quais dias estão marcados.
          Nenhum marcado é "todos". Largura fixa e o -1px em todos (o tema só sobrepõe as bordas do
          botão contornado, não do preenchido): sem os dois o grupo mudava de largura a cada clique e
          empurrava o "Formato" ao lado.
        */}
        <FormField label="Dias" w="auto">
          <ButtonGroup isAttached variant="outline" aria-label="Dias da semana">
            {WEEK_DAYS.map((day) => {
              const isSelected = filter.days.includes(day)

              return (
                <Button
                  key={day}
                  w={14}
                  px={0}
                  _notLast={{ marginEnd: '-1px' }}
                  colorScheme={isSelected ? 'magenta' : 'gray'}
                  variant={isSelected ? 'solid' : 'outline'}
                  aria-pressed={isSelected}
                  aria-label={WEEK_DAY_LABELS[day]}
                  onClick={() => toggleDay(day)}
                >
                  {WEEK_DAY_SHORT_LABELS[day]}
                </Button>
              )
            })}
          </ButtonGroup>
        </FormField>

        <FormField label="Formato" maxW="180px">
          <FormSelect
            value={filter.format}
            onChange={(value) => updateFilter({ format: value as TScheduleFormat | '' })}
            options={Object.entries(FORMAT_LABELS).map(([value, label]) => ({ value: value, label: label }))}
            placeholder="Todos"
          />
        </FormField>
      </Flex>

      {/* Os filtros de liga/desliga ganham linha própria: na fileira dos selects, um quebrava
          de linha e o outro não, conforme a largura da tela. */}
      <Flex gap={6} flexWrap="wrap" mb={3} align="center">
        {/*
          "Só incompletos" descrevia o filtro da época da importação, quando uma turma podia
          chegar da planilha sem curso vinculado. Sem importação (ABRA-315) toda turma nasce
          com curso, e o que este filtro encontra na prática é a turma com professor "A
          Definir" — que é a pendência que a secretaria varre a tela para achar. O parâmetro
          continua sendo only_incomplete: o back segue trazendo também o curso arquivado por
          soft delete, que ainda acontece e ainda precisa aparecer em algum lugar.
        */}
        <Checkbox
          isChecked={filter.only_incomplete}
          onChange={(e) => updateFilter({ only_incomplete: e.target.checked })}
        >
          Sem professor
        </Checkbox>

        {/* Mesmo filtro da página pública do cronograma, com o mesmo corte (ABRA-371). */}
        <Checkbox isChecked={filter.only_upcoming} onChange={(e) => updateFilter({ only_upcoming: e.target.checked })}>
          Só turmas que ainda vão iniciar
        </Checkbox>

        {permissions.add && (
          <Flex gap={3} ml="auto">
            <Button colorScheme="magenta" onClick={openCreate}>
              + Nova turma
            </Button>
          </Flex>
        )}
      </Flex>

      {/*
        O período inteiro vazio troca a tabela por um painel, e não pela linha de vazio: sem
        nenhuma turma o cabeçalho de 13 colunas é ruído, e o que a tela tem a dizer é
        "cadastre a primeira". Com filtro ativo o caminho é o outro — a tabela fica, e quem
        responde é o <TableEmptyRow>, porque o que faltou foi o recorte e não o cadastro.

        Era um bloco desenhado à mão, todo construído em torno da escolha entre importar uma
        planilha e cadastrar à mão; sem a importação (ABRA-315) sobrou uma ação só.
      */}
      {isEmpty && !hasActiveFilters ? (
        <EmptyState
          variant="panel"
          icon="lni lni-calendar"
          title="Nenhuma turma cadastrada neste período"
          description="As turmas de um período saem da data de início de cada uma."
          action={
            permissions.add && (
              <Button colorScheme="magenta" onClick={openCreate}>
                Cadastrar turma
              </Button>
            )
          }
        />
      ) : (
        <>
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  {/* Código abre a linha: é o que a busca casa e o que a ABRA usa para se
                      referir à turma, então precisa estar onde o olho bate primeiro. */}
                  <Th>Código</Th>
                  <Th>Curso</Th>
                  <Th>Formato</Th>
                  <Th>Professor</Th>
                  <Th>Início</Th>
                  <Th>Fim</Th>
                  <Th>Dias</Th>
                  <Th>Turno</Th>
                  <Th>Horário</Th>
                  <Th>Sala</Th>
                  <Th>Alunos</Th>
                  <Th>Status</Th>
                  <Th>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <TableSkeletonRows cols={COLUMN_COUNT} />
                ) : loadError ? (
                  <TableErrorRow colSpan={COLUMN_COUNT} entity="turmas" feminine onRetry={fetchRows} />
                ) : isEmpty ? (
                  <TableEmptyRow colSpan={COLUMN_COUNT} entity="turma" feminine />
                ) : (
                  rows.map((row) => (
                    <Tr key={row.id}>
                      {/* O destaque de cadastro incompleto acompanha a primeira coluna. */}
                      <Td
                        borderLeftWidth={row.is_incomplete ? '4px' : 0}
                        borderLeftColor="orange.400"
                        fontWeight="semibold"
                      >
                        <EmptyCell value={row.code} />
                      </Td>
                      <Td maxW="240px" whiteSpace="normal">
                        <IssueCell issue={courseIssue(row)}>
                          <EmptyCell value={row.course?.name ?? row.course_name_raw} />
                        </IssueCell>
                      </Td>
                      {/* Travessão, e não "Presencial": turma importada de planilha sem a
                          coluna "Formato" não tem modalidade, e supor uma seria inventar dado. */}
                      <Td>
                        <EmptyCell value={row.format && FORMAT_SHORT_LABELS[row.format]} />
                      </Td>
                      {/*
                        Aqui o vazio NÃO é travessão: uma turma sem professor é uma pendência,
                        não um campo em branco. O aviso mudou de coluna junto com ele — ficava
                        na de Curso dizendo "Curso e professor não vinculados", longe da célula
                        que estava vazia (ABRA-315).
                      */}
                      <Td maxW="180px" whiteSpace="normal">
                        <IssueCell issue={teacherIssue(row)}>
                          {row.teacher?.name ?? row.teacher_name_raw ?? TEACHER_PLACEHOLDER}
                        </IssueCell>
                      </Td>
                      <Td>{formatIsoDate(row.start_date)}</Td>
                      <Td>{formatIsoDate(row.end_date)}</Td>
                      <Td maxW="160px" whiteSpace="normal">
                        {row.week_days}
                      </Td>
                      <Td>{SHIFT_SHORT_LABELS[row.shift]}</Td>
                      <Td>
                        {row.start_time.slice(0, 5)}–{row.end_time.slice(0, 5)}
                      </Td>
                      <Td>
                        <EmptyCell value={row.room} />
                      </Td>
                      <Td>
                        {row.enrolled_students}/<EmptyCell value={row.max_students} />
                      </Td>
                      <Td>
                        <ScheduleClassStatusCell
                          scheduleClass={row}
                          canEdit={permissions.edit}
                          onChanged={(status) => handleStatusChanged(row.id, status)}
                        />
                      </Td>
                      <Td>
                        {permissions.edit && (
                          <ActionButton action="edit" label="Editar turma" onClick={() => openEdit(row)} />
                        )}
                        {permissions.delete && (
                          <ActionButton action="delete" label="Excluir turma" onClick={() => setDeletingId(row.id)} />
                        )}
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>

          {/* O Paginator vai direto no Box da página, sem wrapper: ele já se organiza sozinho
              (seletor à esquerda, setas centradas) e precisa da largura toda para isso. Dentro de
              um Flex `direction="column" align="center"` os dois espaçadores `flex="1"` dele
              ficavam sem espaço para distribuir, e o seletor de itens por página colava nas setas
              no meio da tela em vez de ficar à esquerda como nas outras listagens. */}
          <Paginator
            page={filter.page}
            totalPages={totalPages}
            changePage={(page: number) => setFilter((f) => ({ ...f, page }))}
            pageSize={filter.page_size}
            onPageSizeChange={(size) => {
              setFilter({ ...filter, page_size: size, page: 1 })
            }}
          />
          <Text variant="caption" textAlign="center" mt={1}>
            {totalFiltered} {totalFiltered === 1 ? 'turma' : 'turmas'}
          </Text>
        </>
      )}

      <ScheduleClassFormModal
        isOpen={formModal.isOpen}
        onClose={formModal.onClose}
        year={year}
        term={term}
        scheduleClass={editing}
        mode={modalMode}
        options={options}
        onSuccess={(saved) => {
          fetchRows()
          onMutate()

          // A turma pode ter caído em outro período: o backend deriva os dois da data de início.
          // Seguir para lá evita o desfecho de salvar com sucesso e ver a linha sumir da tabela.
          if (saved.year !== year || saved.term !== term) onGoToPeriod(saved.term, saved.year)
        }}
      />

      <ConfirmDialog
        isOpen={deletingId > 0}
        onClose={() => setDeletingId(0)}
        onConfirm={handleDelete}
        action="delete"
        entity="turma"
        feminine
      />
    </Box>
  )
}
